import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Braces } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { filterVars, type VarItem } from "./var-catalog.js";

export interface VarSuggestRef {
  /** Returns true if the key was handled (caller should stop propagation). */
  onKeyDown: (e: KeyboardEvent) => boolean;
}

export interface VarSuggestProps {
  /** Live query typed after `{{`. */
  query: string;
  command: (item: VarItem) => void;
  onClose?: () => void;
}

/**
 * Compact `{{` variable picker — a small cursor-region dropdown listing the
 * `user.*` template variables. Single namespace, so no category sidebar (unlike
 * the `#` command palette); just a filtered list with ↑/↓/↵/esc nav.
 */
export const VarSuggestList = forwardRef<VarSuggestRef, VarSuggestProps>(
  function VarSuggestList({ query, command, onClose }, ref) {
    const items = useMemo(() => filterVars(query), [query]);
    const [index, setIndex] = useState(0);

    useEffect(() => {
      setIndex(0);
    }, [query]);

    const activeRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
      activeRef.current?.scrollIntoView({ block: "nearest" });
    }, [index]);

    useImperativeHandle(ref, () => ({
      onKeyDown: (e: KeyboardEvent): boolean => {
        if (e.key === "ArrowDown") {
          setIndex((i) => (items.length ? (i + 1) % items.length : 0));
          return true;
        }
        if (e.key === "ArrowUp") {
          setIndex((i) => (items.length ? (i - 1 + items.length) % items.length : 0));
          return true;
        }
        if (e.key === "Enter") {
          const item = items[index];
          if (item) command(item);
          return true;
        }
        if (e.key === "Escape") {
          onClose?.();
          return true;
        }
        return false;
      },
    }));

    return (
      <div
        className="w-64 overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-lg"
        data-testid="var-suggest-list"
      >
        <div className="border-b border-border px-3 py-2 text-xs font-medium text-muted-foreground">
          Contact variables
        </div>
        <div className="max-h-60 overflow-auto p-1.5">
          {items.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">No matches</p>
          ) : (
            items.map((item, i) => (
              <button
                key={item.field}
                ref={i === index ? activeRef : undefined}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  command(item);
                }}
                onMouseEnter={() => setIndex(i)}
                className={cn(
                  "flex h-9 w-full items-center gap-2.5 rounded-md px-2 text-left transition-colors",
                  i === index ? "bg-muted" : "hover:bg-muted/60",
                )}
              >
                <Braces className="h-4 w-4 shrink-0 text-foreground" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">
                  {item.label}
                </span>
                <span className="ml-auto shrink-0 font-mono text-xs text-muted-foreground">
                  {item.token}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    );
  },
);
